import styled, { css } from 'styled-components';
import { generateScalingQueries, generateScales } from '@helpers/generate-media-queries';
import Button from '@components/button';
import { LargeButton } from '@styles/button.styled';

export const SubmitContainer = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: space-between;
    align-items: center;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
    }
  `}
`;

export const HubSpotForm = styled.div`
  ${({ theme }) => css`
    min-width: 40rem;
    width: 100%;

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      min-width: 32rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      min-width: unset;
    }

    .hs-form {
      display: flex;
      flex-direction: column;
      row-gap: 0rem;
      column-gap: 1.5rem;
      width: 100%;

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        grid-template-columns: repeat(1, 1fr);
      }
    }

    .form-columns-2 {
      display: flex;
      flex-direction: row;
      column-gap: 1rem;
      max-width: unset;

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        flex-direction: column;
      }
    }

    .form-columns-1 {
      max-width: unset;
    }

    .form-columns-1 .field,
    .form-columns-2 .field,
    .hs-form .field {
      margin-bottom: 1.5rem;
    }

    .hs-form fieldset.form-columns-1 .input,
    .hs-form fieldset.form-columns-2 .input {
      width: 100%;
      margin-right: 0;

      input.hs-input {
        width: 100%;
      }

      textarea.hs-fieldtype-textarea {
        width: 100%;
      }
    }

    .hs_submit {
      width: 100%;
    }

    .actions {
      width: 100%;
    }

    .hs-button {
      ${LargeButton}
      width: 100%;
      border-color: ${theme.colors.black};
      background: ${theme.colors.black};
      color: ${theme.colors.white};
      transition: transform ${theme.transitions.normal};

      :hover {
        color: ${theme.colors.black};
        background-color: ${theme.colors.white};
        border-color: ${theme.colors.white};
        transform: scale(1.0625);
      }

      :active {
        transform: scale(0.9375);
      }

      :focus {
        outline: 4px solid ${theme.colors.white}44;
      }
    }

    .hs-form-field {
      display: flex;
      flex-direction: column;
      row-gap: 0.0625rem;

      input {
        border-radius: 0.125rem;
        ${generateScales(
          'font-size',
          theme.inputSize.lg.mobile.fontSize,
          theme.inputSize.lg.desktop.fontSize,
          theme
        )}
        ${generateScales('padding-left', '0.5rem', '0.75rem', theme)}
        ${generateScales('padding-right', '0.5rem', '0.75rem', theme)}
        ${generateScales('height', '2.75rem', '3rem', theme)}

        border: 1px solid ${theme.colors.darkPurple};
        background-color: ${theme.colors.white};
        color: ${theme.colors.darkPurple};
        outline: none;
        font-family: ${theme.fontFamily.text};
        transition: all ${theme.transitions.normal};
        border-radius: 0.125rem;
        width: 100%;

        ::placeholder {
          color: ${theme.colors.dark}99;
        }

        :focus {
          border-color: ${theme.colors.dark};
          outline: 4px solid ${theme.colors.secondary}22;
        }
      }

      textarea.hs-fieldtype-textarea {
        border-radius: 0.125rem;
        ${generateScales('padding-left', '0.5rem', '0.75rem', theme)}
        ${generateScales('padding-right', '0.5rem', '0.75rem', theme)}
        ${generateScales('height', '2.75rem', '3rem', theme)}
        border: 1px solid ${theme.colors.darkPurple};
        background-color: ${theme.colors.white};
        color: ${theme.colors.darkPurple};
        outline: none;
        font-family: ${theme.fontFamily.text};
        transition: all ${theme.transitions.normal};
        min-height: 10rem;
        max-width: 100%;
        width: 100%;
      }

      label {
        font-weight: 400;
        font-family: ${theme.fontFamily.display};
        font-size: ${theme.fontSize.small.desktop};
        line-height: ${theme.lineHeights.xs};
        color: ${theme.colors.white};
        margin: 0 0 ${theme.spacing.xs} 0;
        ${generateScales(
          'font-size',
          theme.fontSize.small.mobile,
          theme.fontSize.small.desktop,
          theme
        )}
        ${generateScalingQueries('margin-bottom', theme.spacing.xs, theme)};

        &.hs-error-msg {
          font-family: ${theme.fontFamily.additional};
          color: ${theme.colors.tertiary};
          font-size: ${theme.fontSize.extraSmall.desktop};
        }
      }
    }
  `}
`;

export const StyledButton = styled(Button)`
  width: 100%;
  color: ${({ theme }) => theme.colors.black} !important;
`;

export const StyledFormContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    background-color: ${theme.backgroundColors.primary};
    border-radius: 2rem;
    padding: 4rem 3.5rem;
    justify-content: center;
    align-items: flex-start;
    min-width: 44rem;
    gap: 4rem;

    @media only screen and (max-width: ${theme.breakpoints.xxl}) {
      gap: 3.5rem;
      padding: 4rem 3rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      min-width: 36rem;
      gap: 3rem;
      padding: 3.5rem 2.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      flex-direction: column;
      min-width: unset;
      gap: 2.5rem;
      border-radius: 1.5rem;
      padding: 3rem 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      gap: 2rem;
      padding: 2.5rem 1.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 1.5rem;
      border-radius: 1rem;
      padding: 2rem 1rem;
    }
  `}
`;

export const FormInputs = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    ${generateScales('row-gap', '1.25rem', '2.5rem', theme)}
    min-width: 40rem;
    width: 100%;

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      min-width: 32rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      min-width: unset;
    }
  `}
`;

export const TitleContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 1rem;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      align-items: center;
      gap: 0.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      text-align: center;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      align-items: flex-start;
      text-align: left;
    }
  `}
`;

export const FormRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;
    flex-direction: row;
    ${generateScales('column-gap', '1rem', '1.5rem', theme)}

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
      row-gap: 1rem;
    }
  `}
`;

export const Fields = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    ${generateScales('row-gap', '1rem', '1.5rem', theme)}
    width: 100%;
  `}
`;

export const ConfirmationWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-content: center;
    row-gap: 0.25rem;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: ${theme.backgroundColors.white};
    padding: 2rem;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      padding: 2rem 1.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      padding: 2rem 1rem;
    }
  `}
`;
